'use client';

import React, { useState } from 'react';
import { ClipboardIcon, CheckCircleIcon } from '../../Icons';
import { PinterestSaveButton } from '../../PinterestSaveButton';
import { getCanonicalUrl } from '../../utils/seo';
import { useUnits } from '../../../contexts/UnitContext';
import { ProfessionalPrintLayout } from '../../ProfessionalPrintLayout';

interface Ingredient {
    id: number;
    name: string;
    quantity: string;
    unit: string;
    weight: string;
}

export const RecipeScalerClient: React.FC = () => {
    const [ingredients, setIngredients] = useState<Ingredient[]>([{ id: 1, name: 'Flour', quantity: '2', unit: 'cups', weight: '240' }]);
    const [scaleFactor, setScaleFactor] = useState('2');
    const [projectName, setProjectName] = useState('');
    const [copied, setCopied] = useState(false);
    const { activeUnit, convertValue, formatValue } = useUnits();

    const handleIngredientChange = (id: number, field: keyof Omit<Ingredient, 'id'>, value: string) => {
        if ((field === 'quantity' || field === 'weight') && parseFloat(value) < 0) return;
        setIngredients(ingredients.map(ing => ing.id === id ? { ...ing, [field]: value } : ing));
    };

    const addIngredient = () => {
        setIngredients([...ingredients, { id: Date.now(), name: '', quantity: '', unit: '', weight: '' }]);
    };

    const removeIngredient = (id: number) => {
        if (ingredients.length > 1) {
            setIngredients(ingredients.filter(ing => ing.id !== id));
        }
    };

    const handleScaleFactorChange = (value: string) => {
        if (parseFloat(value) < 0) return;
        setScaleFactor(value);
    };

    const handleCopy = async () => {
        const factor = parseFloat(scaleFactor) || 1;
        const recipeText = ingredients.map(ing => {
            const originalQty = parseFloat(ing.quantity) || 0;
            const originalWeight = parseFloat(ing.weight) || 0;
            const newQty = (originalQty * factor).toLocaleString(undefined, { maximumFractionDigits: 2 });
            const finalWeightVal = originalWeight * factor;
            const convertedWeight = convertValue(finalWeightVal, 'g', activeUnit);
            
            let resultText = '';
            if (originalQty > 0 && ing.unit) {
                resultText += `${newQty} ${ing.unit}`;
            }
            if (originalWeight > 0) {
                if (resultText) resultText += ' / ';
                resultText += `${formatValue(convertedWeight)} ${activeUnit}`;
            }
            return `${ing.name}: ${resultText}`;
        }).join('\n');

        try {
            await navigator.clipboard.writeText(recipeText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            alert('Failed to copy.');
        }
    };

    return (
        <>
            <div className="grid xl:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-serif font-bold text-2xl text-brand-text-title">Original Recipe</h3>
                    </div>

                    <div className="space-y-3 mb-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <div className="hidden lg:flex gap-2 mb-2 px-2 text-xs font-bold text-brand-text-body/60 uppercase tracking-wider">
                            <div className="flex-grow">Ingredient</div>
                            <div className="w-20 text-center">Qty</div>
                            <div className="w-24">Unit</div>
                            <div className="w-24">Weight (g)</div>
                            <div className="w-8"></div>
                        </div>

                        {ingredients.map((ing, index) => (
                            <div key={ing.id} className="relative flex flex-col lg:flex-row gap-2 items-start lg:items-center group bg-white p-3 rounded-lg border border-gray-200 lg:border-none lg:bg-transparent lg:p-0 shadow-sm lg:shadow-none">
                                <input
                                    type="text"
                                    placeholder={`Ingredient ${index + 1}`}
                                    value={ing.name}
                                    onChange={e => handleIngredientChange(ing.id, 'name', e.target.value)}
                                    className="w-full px-3 py-2 bg-white border border-brand-border rounded-lg text-sm focus:ring-2 focus:ring-brand-pink/50 focus:border-brand-pink"
                                />
                                <div className="grid grid-cols-3 lg:flex gap-2 w-full lg:w-auto items-center">
                                    <input
                                        type="number"
                                        placeholder="Qty"
                                        value={ing.quantity}
                                        onChange={e => handleIngredientChange(ing.id, 'quantity', e.target.value)}
                                        className="w-full lg:w-20 px-2 py-2 bg-white border border-brand-border rounded-lg text-sm focus:ring-2 focus:ring-brand-pink/50 focus:border-brand-pink text-center"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Unit"
                                        value={ing.unit}
                                        onChange={e => handleIngredientChange(ing.id, 'unit', e.target.value)}
                                        className="w-full lg:w-24 px-2 py-2 bg-white border border-brand-border rounded-lg text-sm focus:ring-2 focus:ring-brand-pink/50 focus:border-brand-pink"
                                    />
                                    <div className="relative w-full lg:w-24">
                                        <input
                                            type="number"
                                            placeholder="g"
                                            value={ing.weight}
                                            onChange={e => handleIngredientChange(ing.id, 'weight', e.target.value)}
                                            className="w-full px-2 py-2 bg-white border border-brand-border rounded-lg text-sm pr-5"
                                        />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs italic">g</span>
                                    </div>
                                    <button onClick={() => removeIngredient(ing.id)} className="lg:p-2 text-gray-400 hover:text-red-500 transition-colors">
                                        &times;
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button onClick={addIngredient} className="w-full py-2 border-2 border-dashed border-brand-pink/30 text-brand-pink font-bold rounded-lg hover:bg-brand-pink/5 transition-colors">
                            + Add Ingredient
                        </button>
                    </div>

                    <div className="bg-brand-pink/5 p-6 rounded-xl border border-brand-pink/20">
                        <label htmlFor="scaleFactor" className="block text-lg font-bold text-brand-text-title mb-2">Recipe Multiplier</label>
                        <div className="flex items-center gap-4">
                            <input
                                id="scaleFactor"
                                type="number"
                                value={scaleFactor}
                                onChange={e => handleScaleFactorChange(e.target.value)}
                                className="w-24 px-4 py-3 bg-white border border-brand-pink/30 rounded-lg text-xl font-bold text-brand-pink text-center"
                                min="0"
                                step="any"
                            />
                            <div className="text-sm text-brand-text-body/70 italic">
                                e.g. 0.5 = Half, 2.0 = Double
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-0 lg:pl-8">
                    <div className="bg-brand-pink-light p-8 rounded-2xl border border-brand-pink/20 h-full flex flex-col">
                        <div className="flex justify-between items-start mb-6">
                            <h3 className="font-serif font-bold text-3xl text-brand-text-title">Scaled Recipe</h3>
                            <span className="bg-brand-pink text-white px-3 py-1 rounded-full text-sm font-bold">{scaleFactor}x</span>
                        </div>

                        <div className="mb-6">
                            <label className="block text-xs font-bold text-brand-text-body/60 mb-1 uppercase tracking-wide">Recipe Name / Customer</label>
                            <input 
                                type="text" 
                                placeholder="e.g. My Custom Batch"
                                value={projectName}
                                onChange={(e) => setProjectName(e.target.value)}
                                className="w-full px-4 py-2.5 bg-white border border-brand-pink/20 rounded-xl text-sm focus:ring-2 focus:ring-brand-pink"
                            />
                        </div>

                        <ul className="space-y-3 flex-grow mb-8">
                            {ingredients.filter(ing => ing.name || ing.weight || ing.quantity).map((ing, i) => {
                                const factor = parseFloat(scaleFactor) || 1;
                                const qty = parseFloat(ing.quantity) || 0;
                                const weight = parseFloat(ing.weight) || 0;
                                const newQty = (qty * factor).toLocaleString(undefined, { maximumFractionDigits: 2 });
                                const finalWeightVal = weight * factor;
                                const convertedWeight = convertValue(finalWeightVal, 'g', activeUnit);

                                return (
                                    <li key={ing.id} className="flex justify-between items-center p-4 bg-white rounded-xl shadow-sm border border-brand-pink/5 hover:border-brand-pink transition-colors">
                                        <span className="font-medium text-brand-text-body text-lg">{ing.name || `Ingredient ${i + 1}`}</span>
                                        <div className="font-bold text-brand-pink text-xl text-right">
                                            {qty > 0 && <div>{newQty} {ing.unit}</div>}
                                            {weight > 0 && (
                                                <div className="text-sm text-brand-pink/60">
                                                    {qty > 0 && <span className="mr-1">/</span>}
                                                    {formatValue(convertedWeight)} {activeUnit}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <button onClick={handleCopy} className="py-4 bg-brand-pink text-white font-bold rounded-xl shadow-md hover:bg-brand-pink-dark transition-all flex items-center justify-center gap-2">
                                {copied ? <CheckCircleIcon className="w-6 h-6" /> : <ClipboardIcon className="w-6 h-6" />}
                                {copied ? 'Copied!' : 'Copy Recipe'}
                            </button>
                            <PinterestSaveButton 
                                url={getCanonicalUrl('/tools/recipe-scaler')}
                                media={getCanonicalUrl('/scalers/recipe-scaler.jpg')}
                                description={`Scaled my recipe to ${scaleFactor}x using Sweety's Bakery!`}
                                className="w-full justify-center"
                            />
                            <button onClick={() => window.print()} className="py-4 bg-brand-text-title hover:bg-black text-white rounded-xl font-bold transition-all shadow-md active:scale-95 flex items-center justify-center gap-2">
                                <span>PDF</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ProfessionalPrintLayout 
                title="Scaled Recipe Sheet"
                subtitle={projectName || "Custom Batch"}
                items={ingredients.filter(ing => ing.name || ing.weight || ing.quantity).map(ing => {
                    const factor = parseFloat(scaleFactor) || 1;
                    const finalW = (parseFloat(ing.weight) || 0) * factor;
                    const cW = convertValue(finalW, 'g', activeUnit);
                    let v = '';
                    if (ing.quantity && ing.unit) v += `${(parseFloat(ing.quantity) * factor).toLocaleString()} ${ing.unit}`;
                    if (finalW > 0) {
                        if (v) v += ' / ';
                        v += `${formatValue(cW)} ${activeUnit}`;
                    }
                    return { name: ing.name || "Ingredient", value: v };
                })}
                total={{ label: "Batch Multiplier", value: `${scaleFactor}x` }}
                footerText={`Calculated on Sweety's Bakery. Unit preference: ${activeUnit.toUpperCase()}.`}
            />
        </>
    );
};
